// Fixed SignupFormFields component with resolved issues
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { StringField } from "@forms/core/StringField";
import {
  Header,
  Checkbox,
  Form,
  Message,
  Popup,
  Icon,
  Dropdown,
  Grid,
} from "semantic-ui-react";
import { Field } from "formik";
const mobileBreakpoint = 768;
const FORM_FIELDS = [
  {
    fieldPath: "email",
    placeholder: "Email Address",
    type: "email",
    icon: "user",
    tabIndex: 1,
  },
  {
    fieldPath: "full_name",
    placeholder: "Full Name",
    type: "text",
    icon: "address card outline",
    tabIndex: 2,
  },
  {
    fieldPath: "username",
    placeholder: "Username",
    type: "text",
    icon: "user outline",
    tabIndex: 3,
  },
  {
    fieldPath: "password",
    placeholder: "Password",
    type: "password",
    icon: "lock",
    tabIndex: 4,
  },
  {
    fieldPath: "password_confirm",
    placeholder: "Confirm Password",
    type: "password",
    icon: "lock",
    tabIndex: 5,
  },
];

const MEMBER_TYPE_OPTIONS = [
  {
    key: "patron",
    label: "Patron",
    value: "patron",
    tooltip:
      "General library users including students, faculty, visitors, and researchers",
    icon: "users",
    requiresSubtype: true,
    tabIndex: 6,
  },
  {
    key: "librarian",
    label: "Librarian",
    value: "librarian",
    tooltip: "Library staff members responsible for managing library resources",
    icon: "book",
    requiresSubtype: false,
    tabIndex: 7,
  },
];

const PATRON_TYPE_OPTIONS = [
  {
    key: "student",
    text: "Student",
    value: "student",
    content: (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Icon name="graduation cap" />
        <div>
          <div style={{ fontWeight: "bold" }}>Student</div>
          <div style={{ fontSize: "0.85em", color: "#666" }}>
            Enrolled students at the institution
          </div>
        </div>
      </div>
    ),
  },
  {
    key: "faculty",
    text: "Faculty",
    value: "faculty",
    content: (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Icon name="user" />
        <div>
          <div style={{ fontWeight: "bold" }}>Faculty</div>
          <div style={{ fontSize: "0.85em", color: "#666" }}>
            Teaching faculty, professors, and academic staff
          </div>
        </div>
      </div>
    ),
  },
  {
    key: "visitor",
    text: "Visitor",
    value: "visitor",
    content: (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Icon name="user plus" />
        <div>
          <div style={{ fontWeight: "bold" }}>Visitor</div>
          <div style={{ fontSize: "0.85em", color: "#666" }}>
            External visitors and guest researchers
          </div>
        </div>
      </div>
    ),
  },
  {
    key: "researcher",
    text: "Researcher",
    value: "researcher",
    content: (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Icon name="lab" />
        <div>
          <div style={{ fontWeight: "bold" }}>Researcher</div>
          <div style={{ fontSize: "0.85em", color: "#666" }}>
            Research staff and independent researchers
          </div>
        </div>
      </div>
    ),
  },
];

const DEFAULT_FIELD_PROPS = {
  iconPosition: "left",
};

const PatronTypeSelector = ({
  selectedPatronType,
  onPatronTypeChange,
  isSubmitting,
  isVisible,
  isMobile,
}) => {
  if (!isVisible) return null;

  const handlePatronTypeChange = (event, { value }) => {
    if (onPatronTypeChange && typeof onPatronTypeChange === "function") {
      onPatronTypeChange(value);
    }
  };

  return (
    <div
      style={{
        marginTop: "1rem",
        marginLeft: isMobile ? "1rem" : "2rem",
        marginRight: isMobile ? "1rem" : "0",
      }}
    >
      <Header
        as="h6"
        style={{
          marginBottom: "0.8em",
          color: "#2c5f2d",
          fontSize: isMobile ? "0.85em" : "0.9em",
          fontWeight: "600",
        }}
      >
        Select Patron Type *
      </Header>

      <Field name="patron_type">
        {({ field, form, meta }) => (
          <Form.Field error={meta.touched && !!meta.error}>
            <Dropdown
              placeholder="Choose your patron type..."
              fluid
              selection
              search={false}
              value={field.value || selectedPatronType}
              options={PATRON_TYPE_OPTIONS}
              onChange={(event, data) => {
                handlePatronTypeChange(event, data);
                form.setFieldValue("patron_type", data.value);
                form.setFieldTouched("patron_type", true);
              }}
              onBlur={() => {
                form.setFieldTouched("patron_type", true);
              }}
              disabled={isSubmitting}
              tabIndex={8}
              style={{
                backgroundColor: "#f5f5f5",
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                fontSize: isMobile ? "13px" : "14px",
                minHeight: isMobile ? "2.5em" : "2.71428571em",
              }}
              error={meta.touched && !!meta.error}
            />
            {meta.touched && meta.error && String(meta.error).trim() !== "" && (
              <Message negative size="mini" style={{ marginTop: "0.3em" }}>
                <Message.Content>{meta.error}</Message.Content>
              </Message>
            )}
          </Form.Field>
        )}
      </Field>
    </div>
  );
};

const MemberTypeField = ({
  selectedTypes = [],
  onTypeChange,
  isSubmitting,
  selectedPatronType,
  onPatronTypeChange,
  isMobile,
}) => {
  const [initialized, setInitialized] = React.useState(false);

  return (
    <Field name="member_types">
      {({ field, form, meta }) => {
        // Initialize with defaults if no value exists
        const currentValue = field.value || [];
        const hasNoMemberTypes =
          !Array.isArray(currentValue) || currentValue.length === 0;

        // Set defaults only once during initial render
        if (hasNoMemberTypes && !initialized) {
          form.setFieldValue("member_types", ["patron"], false);
          if (onTypeChange) onTypeChange(["patron"]);

          const currentPatronType = form.values?.patron_type;
          if (!currentPatronType) {
            form.setFieldValue("patron_type", "student", false);
            if (onPatronTypeChange) onPatronTypeChange("student");
          }
          setInitialized(true);
        }

        const effectiveSelected =
          Array.isArray(currentValue) && currentValue.length > 0
            ? currentValue
            : selectedTypes;

        const handleCheckboxChange = (event, { value, checked }) => {
          let newSelectedTypes;
          if (checked) {
            newSelectedTypes = [value]; // Radio-like behavior

            if (value === "patron") {
              const currentType = form.values?.patron_type;
              if (!currentType) {
                form.setFieldValue("patron_type", "student");
                if (onPatronTypeChange) onPatronTypeChange("student");
              }
            }
          } else {
            newSelectedTypes = [];
            if (value === "patron") {
              if (onPatronTypeChange) onPatronTypeChange(null);
              form.setFieldValue("patron_type", null);
            }
          }

          if (onTypeChange) onTypeChange(newSelectedTypes);
          form.setFieldValue("member_types", newSelectedTypes);
          form.setFieldTouched("member_types", true);
        };

        return (
          <Form.Field error={meta.touched && !!meta.error}>
            <div
              style={{
                border: "1px solid #d4d4d5",
                borderRadius: "12px",
                padding: isMobile ? "0.75rem" : "1rem",
                backgroundColor: "#f8f9fa",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  fontSize: isMobile ? "0.8em" : "0.85em",
                  color: "rgb(0 0 0)",
                  marginBottom: "1rem",
                  padding: isMobile ? "0.6em" : "0.8em",
                  backgroundColor: "#fbbd084d",
                  borderRadius: "12px",
                  textAlign: "left",
                  lineHeight: "1.3",
                }}
              >
                <strong>Choose your primary role:</strong> Select the role that
                best describes your relationship with the library.
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: isMobile ? "0.75rem" : "1rem",
                }}
              >
                {MEMBER_TYPE_OPTIONS.map((option) => {
                  const isSelected = effectiveSelected.includes(option.value);

                  return (
                    <div key={option.key}>
                      <div
                        style={{
                          padding: isMobile ? "0.75rem" : "1rem",
                          border: isSelected
                            ? "2px solid #2c5f2d"
                            : "1px solid #d4d4d5",
                          borderRadius: "12px",
                          backgroundColor: isSelected ? "#f0f8f0" : "white",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          display: "flex",
                          alignItems: isMobile ? "flex-start" : "center",
                          gap: isMobile ? "0.6rem" : "0.8rem",
                          flexDirection: isMobile ? "column" : "row",
                        }}
                        onClick={(e) => {
                          if (e.target.type !== "checkbox") {
                            handleCheckboxChange(e, {
                              value: option.value,
                              checked: !isSelected,
                            });
                          }
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: isMobile ? "0.6rem" : "0.8rem",
                            width: "100%",
                          }}
                        >
                          <Checkbox
                            value={option.value}
                            checked={isSelected}
                            onChange={handleCheckboxChange}
                            disabled={isSubmitting}
                            tabIndex={option.tabIndex}
                            onClick={(e) => e.stopPropagation()}
                            className="aligned-checkbox"
                          />
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: "0.3rem",
                            }}
                          >
                            <Icon name={option.icon} />
                            <span
                              style={{
                                fontSize: isMobile ? "0.9em" : "1em",
                                fontWeight: "bold",
                                color: "#2c5f2d",
                              }}
                            >
                              {option.label}
                            </span>
                            <Popup
                              content={option.tooltip}
                              trigger={
                                <Icon
                                  name="info circle"
                                  size="small"
                                  style={{
                                    color: "#2185d0",
                                    cursor: "help",
                                    opacity: 0.7,
                                  }}
                                />
                              }
                              position="top center"
                              size="small"
                              inverted
                              style={{
                                maxWidth: isMobile ? "250px" : "300px",
                                fontSize: "0.85em",
                                textAlign: "center",
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {option.requiresSubtype && (
                        <PatronTypeSelector
                          selectedPatronType={selectedPatronType}
                          onPatronTypeChange={onPatronTypeChange}
                          isSubmitting={isSubmitting}
                          isVisible={isSelected}
                          isMobile={isMobile}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {meta.touched && meta.error && (
              <Message negative size="mini" style={{ marginTop: "0.3em" }}>
                <Message.Content>{meta.error}</Message.Content>
              </Message>
            )}
          </Form.Field>
        );
      }}
    </Field>
  );
};

MemberTypeField.propTypes = {
  selectedTypes: PropTypes.array,
  onTypeChange: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  selectedPatronType: PropTypes.string,
  onPatronTypeChange: PropTypes.func,
  isMobile: PropTypes.bool.isRequired,
};

PatronTypeSelector.propTypes = {
  selectedPatronType: PropTypes.string,
  onPatronTypeChange: PropTypes.func,
  isSubmitting: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

const SignupFormFields = ({
  isSubmitting,
  memberTypes = ["patron"],
  onMemberTypeChange,
  selectedPatronType = "student",
  onPatronTypeChange,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < mobileBreakpoint);
      setIsTablet(width >= mobileBreakpoint && width < 992);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const responsiveStyles = `
    .custom-input-field .ui.input > input {
      background-color: #f5f5f5 !important;
      border: 1px solid #e0e0e0 !important;
      border-radius: 12px !important;
      padding: ${
        isMobile ? "14px 14px 14px 40px" : "16px 16px 16px 45px"
      } !important;
      font-size: ${isMobile ? "13px" : "14px"} !important;
      color: #333 !important;
      transition: all 0.3s ease !important;
      height: ${isMobile ? "46px" : "50px"} !important;
    }
    
    .custom-input-field .ui.input > input:focus {
      background-color: #ffffff !important;
      border-color: #2c5f2d !important;
      box-shadow: 0 0 0 2px rgba(44, 95, 45, 0.1) !important;
    }
    
    .custom-input-field .ui.input > input::placeholder {
      color: #999 !important;
      font-weight: 400 !important;
      font-size: ${isMobile ? "12px" : "14px"} !important;
    }
    
    .custom-input-field .ui.input > i.icon {
      color: #666 !important;
      left: ${isMobile ? "12px" : "16px"} !important;
      opacity: 0.7 !important;
      font-size: ${isMobile ? "1em" : "1.18em"} !important;
    }
    
    .custom-input-field .ui.input {
      width: 100% !important;
    }
    
    .ui.dropdown {
      min-height: ${isMobile ? "2.5em" : "2.71428571em"} !important;
      font-size: ${isMobile ? "13px" : "14px"} !important;
    }
    
    .ui.dropdown > .dropdown.icon {
      opacity: 0.7 !important;
    }
    
    .ui.dropdown .menu > .item {
      padding: ${isMobile ? "0.6rem 0.8rem" : "0.8rem 1rem"} !important;
    }
    
    @media (max-width: 767px) {
      .ui.grid > .row > .column {
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;
      }
      
      .ui.stackable.grid > .row > .column {
        margin-bottom: 0.5rem !important;
      }
    }
    
    @media (min-width: 768px) and (max-width: 991px) {
      .custom-input-field .ui.input > input {
        font-size: 13px !important;
        height: 48px !important;
      }
    }
    
    @media (max-width: 767px) {
      .ui.checkbox {
        min-height: 44px !important;
        display: flex !important;
        align-items: center !important;
      }
      
      .ui.checkbox input ~ label:before,
      .ui.checkbox input ~ label:after {
        top: 50% !important;
        transform: translateY(-50%) !important;
        margin-top: 0 !important;
      }
    }
  `;

  const getFieldSpacing = () => {
    if (isMobile) return "0.75rem";
    if (isTablet) return "0.85rem";
    return "1rem";
  };

  const getGridColumns = () => {
    return isMobile ? 16 : 8;
  };

  return (
    <div style={{ padding: "0" }}>
      <style>{responsiveStyles}</style>

      <div style={{ marginBottom: isMobile ? "1.5rem" : "2rem" }}>
        <Header
          as="h5"
          style={{
            marginBottom: "1em",
            color: "#2c5f2d",
            fontSize: isMobile ? "0.9em" : "1em",
            textAlign: "left",
            fontWeight: "600",
          }}
        >
          Account Information
        </Header>

        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={getGridColumns()}>
              <div style={{ marginBottom: getFieldSpacing() }}>
                <StringField
                  {...DEFAULT_FIELD_PROPS}
                  {...FORM_FIELDS.find((field) => field.fieldPath === "email")}
                  disabled={isSubmitting}
                  tabIndex={1}
                  style={{ width: "100%", fontSize: "0.9em" }}
                  size={isMobile ? "medium" : "large"}
                  className="custom-input-field"
                />
              </div>

              <div style={{ marginBottom: getFieldSpacing() }}>
                <StringField
                  {...DEFAULT_FIELD_PROPS}
                  {...FORM_FIELDS.find(
                    (field) => field.fieldPath === "full_name"
                  )}
                  disabled={isSubmitting}
                  tabIndex={2}
                  style={{ width: "100%", fontSize: "0.9em" }}
                  size={isMobile ? "medium" : "large"}
                  className="custom-input-field"
                />
              </div>

              <div style={{ marginBottom: isMobile ? getFieldSpacing() : "0" }}>
                <StringField
                  {...DEFAULT_FIELD_PROPS}
                  {...FORM_FIELDS.find(
                    (field) => field.fieldPath === "username"
                  )}
                  disabled={isSubmitting}
                  tabIndex={3}
                  style={{ width: "100%", fontSize: "0.9em" }}
                  size={isMobile ? "medium" : "large"}
                  className="custom-input-field"
                />
              </div>
            </Grid.Column>

            {!isMobile && (
              <Grid.Column width={8}>
                <div style={{ marginBottom: getFieldSpacing() }}>
                  <StringField
                    {...DEFAULT_FIELD_PROPS}
                    {...FORM_FIELDS.find(
                      (field) => field.fieldPath === "password"
                    )}
                    disabled={isSubmitting}
                    tabIndex={4}
                    style={{ width: "100%", fontSize: "0.9em" }}
                    size={isTablet ? "medium" : "large"}
                    className="custom-input-field"
                  />
                </div>

                <div style={{ marginBottom: getFieldSpacing() }}>
                  <StringField
                    {...DEFAULT_FIELD_PROPS}
                    {...FORM_FIELDS.find(
                      (field) => field.fieldPath === "password_confirm"
                    )}
                    disabled={isSubmitting}
                    tabIndex={5}
                    style={{ width: "100%", fontSize: "0.9em" }}
                    size={isTablet ? "medium" : "large"}
                    className="custom-input-field"
                  />
                </div>

                <div style={{ height: isTablet ? "54px" : "66px" }} />
              </Grid.Column>
            )}
          </Grid.Row>

          {isMobile && (
            <Grid.Row>
              <Grid.Column width={16}>
                <div style={{ marginBottom: getFieldSpacing() }}>
                  <StringField
                    {...DEFAULT_FIELD_PROPS}
                    {...FORM_FIELDS.find(
                      (field) => field.fieldPath === "password"
                    )}
                    disabled={isSubmitting}
                    tabIndex={4}
                    style={{ width: "100%", fontSize: "0.9em" }}
                    size="medium"
                    className="custom-input-field"
                  />
                </div>
                <div style={{ marginBottom: getFieldSpacing() }}>
                  <StringField
                    {...DEFAULT_FIELD_PROPS}
                    {...FORM_FIELDS.find(
                      (field) => field.fieldPath === "password_confirm"
                    )}
                    disabled={isSubmitting}
                    tabIndex={5}
                    style={{ width: "100%", fontSize: "0.9em" }}
                    size="medium"
                    className="custom-input-field"
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      </div>

      <div>
        <MemberTypeField
          selectedTypes={memberTypes}
          onTypeChange={onMemberTypeChange}
          isSubmitting={isSubmitting}
          selectedPatronType={selectedPatronType}
          onPatronTypeChange={onPatronTypeChange}
          isMobile={isMobile}
        />
      </div>
    </div>
  );
};

SignupFormFields.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
  memberTypes: PropTypes.array,
  onMemberTypeChange: PropTypes.func.isRequired,
  selectedPatronType: PropTypes.string,
  onPatronTypeChange: PropTypes.func,
};

SignupFormFields.defaultProps = {
  memberTypes: ["patron"],
  selectedPatronType: "student",
  onPatronTypeChange: () => {},
};

export default SignupFormFields;
